import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { levelInputSchema } from '@/lib/schemas/frameworkConfig'
import type { LevelInput } from '@/lib/api/frameworkConfig'
import type { VersionedLevelLabels } from './types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'

/** Build a Zod schema for exactly N levels. */
function buildLevelsSchema(levelCount: number) {
  return z.object({
    levels: z.array(levelInputSchema).length(levelCount, `Exactly ${levelCount} levels required`),
  })
}

type LevelsFormData = z.infer<ReturnType<typeof buildLevelsSchema>>

export interface LevelEditorDialogProps {
  mode: 'create' | 'edit'
  levelCount: number
  versionNumber?: number
  initialLevels: LevelInput[]
  isPending: boolean
  labels: VersionedLevelLabels
  saveLabel: string
  createLabel: string
  savingLabel: string
  cancelLabel: string
  onSubmit: (levels: LevelInput[]) => void
  onCancel: () => void
}

export function LevelEditorDialog({
  mode,
  levelCount,
  versionNumber,
  initialLevels,
  isPending,
  labels,
  saveLabel,
  createLabel,
  savingLabel,
  cancelLabel,
  onSubmit,
  onCancel,
}: LevelEditorDialogProps) {
  // Pad or trim to match expected count
  const paddedLevels = Array.from({ length: levelCount }, (_, i) => ({
    level_number: i + 1,
    label: initialLevels[i]?.label ?? '',
    description: initialLevels[i]?.description ?? '',
  }))

  const schema = buildLevelsSchema(levelCount)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LevelsFormData>({
    resolver: zodResolver(schema),
    defaultValues: { levels: paddedLevels },
  })

  function handleFormSubmit(data: LevelsFormData) {
    onSubmit(
      data.levels.map((l, i) => ({
        level_number: i + 1,
        label: l.label,
        description: l.description || undefined,
      })),
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <Card className="relative z-10 mx-4 flex max-h-[80vh] w-full max-w-lg flex-col">
        <CardHeader>
          <CardTitle>
            {mode === 'create'
              ? labels.createDraft
              : `${labels.editDraft} — ${labels.versionLabel(versionNumber ?? 0)}`}
          </CardTitle>
          <CardDescription>{labels.levelCountInfo(levelCount)}</CardDescription>
        </CardHeader>
        <CardContent className="overflow-y-auto">
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            {/* Column headers */}
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <div className="w-10 text-center">#</div>
              <div className="flex-1">{labels.levelLabel}</div>
              <div className="flex-1">{labels.levelDescription}</div>
            </div>

            {/* Fixed level rows */}
            <div className="space-y-3">
              {paddedLevels.map((_, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-10 pt-2 text-center text-sm font-medium text-muted-foreground">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-1">
                    <Input placeholder={labels.levelLabel} {...register(`levels.${index}.label`)} />
                    {errors.levels?.[index]?.label && (
                      <p className="text-xs text-destructive">
                        {errors.levels[index]?.label?.message}
                      </p>
                    )}
                  </div>
                  <div className="flex-1 space-y-1">
                    <Input
                      placeholder={labels.levelDescription}
                      {...register(`levels.${index}.description`)}
                    />
                  </div>
                </div>
              ))}
            </div>

            {errors.levels?.message && (
              <p className="text-sm text-destructive">{errors.levels.message}</p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                {cancelLabel}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {savingLabel}
                  </>
                ) : mode === 'create' ? (
                  createLabel
                ) : (
                  saveLabel
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
